import React from 'react';
import PropTypes from 'prop-types';
import DateTimeDisplay from '../common/DateTimeDisplay';
import StatePill from './StatePill';
import { syncState } from './integrationStates';

function triggerLabel(trigger) {
  if (trigger === 'scheduled') return 'Scheduled';
  if (trigger === 'manual') return 'Manual';
  return trigger || '—';
}

export default function IntegrationSyncLogPanel({ syncRun, loading }) {
  if (loading) {
    return <div className="text-muted small py-4 text-center">Loading sync log…</div>;
  }

  if (!syncRun) {
    return (
      <div className="text-muted small py-4 text-center">
        No sync runs recorded yet. Use &ldquo;Sync now&rdquo; from the integrations list to run a sync.
      </div>
    );
  }

  const assetErrors = Array.isArray(syncRun.asset_errors) ? syncRun.asset_errors : [];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="border rounded-3 p-3 bg-light">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <StatePill state={syncState(syncRun.status)} />
          <span className="small text-muted">{triggerLabel(syncRun.trigger)} run</span>
        </div>
        <div className="row g-3 small">
          <div className="col-6">
            <div className="text-muted">Started</div>
            <DateTimeDisplay isoString={syncRun.started_at || syncRun.created_at} />
          </div>
          <div className="col-6">
            <div className="text-muted">Finished</div>
            <DateTimeDisplay isoString={syncRun.finished_at || syncRun.created_at} />
          </div>
          <div className="col-4">
            <div className="text-muted">Assets</div>
            <div>{syncRun.asset_count ?? 0}</div>
          </div>
          <div className="col-4">
            <div className="text-muted">Synced</div>
            <div>{syncRun.synced_count ?? 0}</div>
          </div>
          <div className="col-4">
            <div className="text-muted">Errors</div>
            <div>{syncRun.error_count ?? 0}</div>
          </div>
        </div>
        {syncRun.error_summary && (
          <div className="mt-3 small text-danger">{syncRun.error_summary}</div>
        )}
      </div>

      {assetErrors.length > 0 ? (
        <div>
          <h6 className="text-muted fw-normal mb-2">Per-asset errors</h6>
          <div
            className="font-monospace small bg-dark text-light rounded p-3 overflow-auto"
            style={{ maxHeight: 320 }}
          >
            {assetErrors.map((entry, index) => (
              <div key={`${entry.external_id || 'global'}-${index}`} className="mb-2">
                {entry.external_id ? (
                  <>
                    <span className="text-warning">{entry.external_id}</span>
                    <span className="text-secondary"> — </span>
                  </>
                ) : null}
                <span>{entry.error}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-muted small mb-0">All linked assets synced successfully.</p>
      )}
    </div>
  );
}

IntegrationSyncLogPanel.propTypes = {
  syncRun: PropTypes.object,
  loading: PropTypes.bool,
};
